import { useState, useEffect } from 'react'
import { Card, Spin, Empty, Button, Progress, Tag, Tooltip } from 'antd'
import ReactECharts from 'echarts-for-react'
import { getStockKlines, getStockValuationHistory } from '@/api'
import { getStockProfile, getStockMeta } from '@/api/stock'
import type { StockBaseMetrics, StockFinancialData, StockKlinePoint, StockProfileResponse } from '@/types/stockPool'
import type { ValuationDetail, ValuationHistoricalItem, StockMetaInfo } from '@/api/stock'
import { buildCandlestickOption } from '@/utils/kline'

const PERIODS = [
    { label: '1月', days: 30 },
    { label: '3月', days: 90 },
    { label: '6月', days: 180 },
    { label: '1年', days: 365 },
    { label: '3年', days: 1095 },
    { label: '5年', days: 1825 },
]

function fmt(val: number | null | undefined, decimals = 2, suffix = ''): string {
    if (val == null || !isFinite(val as number)) return '--'
    return (val as number).toFixed(decimals) + suffix
}

function fmtLarge(val: number | null | undefined): string {
    if (val == null || !isFinite(val as number)) return '--'
    const n = val as number
    if (Math.abs(n) >= 1e8) return (n / 1e8).toFixed(1) + '亿'
    if (Math.abs(n) >= 1e4) return (n / 1e4).toFixed(1) + '万'
    return n.toFixed(0)
}

function pctColor(p: number | null): string {
    if (p == null) return '#d9d9d9'
    if (p < 33) return '#52c41a'
    if (p < 67) return '#faad14'
    return '#ff4d4f'
}

function computePercentile(items: ValuationHistoricalItem[], key: 'pe_ttm' | 'pb_mrq' | 'ps_ttm', current: number): number {
    const values = items.map(v => v[key]).filter((v): v is number => v != null).sort((a, b) => a - b)
    if (values.length === 0) return 50
    return Math.round((values.filter(v => v <= current).length / values.length) * 100)
}

interface Props {
    stockCode: string
    metrics: StockBaseMetrics | null
    latestFinancial: StockFinancialData | null
}

export default function OverviewTab({ stockCode, metrics, latestFinancial }: Props) {
    const [period, setPeriod] = useState(30)
    const [klines, setKlines] = useState<StockKlinePoint[]>([])
    const [klineLoading, setKlineLoading] = useState(false)
    const [klines365, setKlines365] = useState<StockKlinePoint[]>([])
    const [valuation, setValuation] = useState<ValuationDetail | null>(null)
    const [profileData, setProfileData] = useState<StockProfileResponse | null>(null)
    const [metaData, setMetaData] = useState<StockMetaInfo | null>(null)

    useEffect(() => {
        if (!stockCode) return
        setKlines([])
        setKlineLoading(true)
        getStockKlines(stockCode, period)
            .then(setKlines)
            .catch(() => setKlines([]))
            .finally(() => setKlineLoading(false))
    }, [stockCode, period])

    useEffect(() => {
        if (!stockCode) return
        setKlines365([])
        setValuation(null)
        setProfileData(null)
        setMetaData(null)
        // 独立拉取 1Y K 线用于计算 52 周高低，不受显示周期影响
        getStockKlines(stockCode, 365)
            .then(setKlines365)
            .catch(() => setKlines365([]))
        getStockValuationHistory(stockCode, 3)
            .then(d => setValuation(d))
            .catch(() => setValuation(null))
        // profile 来自 market-module（VPC），meta 来自本地 DB（兜底）
        getStockProfile(stockCode)
            .then(setProfileData)
            .catch(() => setProfileData(null))
        getStockMeta(stockCode)
            .then(setMetaData)
            .catch(() => setMetaData(null))
    }, [stockCode])

    // 从 1Y K 线计算 52 周高低价
    const high52w: number | null = klines365.length > 0
        ? Math.max(...klines365.map(k => k.high ?? k.close))
        : (metrics?.high_52w ?? null)
    const low52w: number | null = klines365.length > 0
        ? Math.min(...klines365.map(k => k.low ?? k.close))
        : (metrics?.low_52w ?? null)

    const metricItems = [
        { label: 'PE (TTM)', value: fmt(metrics?.pe_ttm) },
        { label: 'PB', value: fmt(metrics?.pb_ratio) },
        { label: 'PS', value: fmt(metrics?.ps_ratio) },
        { label: 'ROE', value: fmt(metrics?.roe_pct, 1, '%') },
        { label: '股息率', value: fmt(metrics?.dividend_yield_pct, 2, '%') },
        { label: '市值', value: fmtLarge(metrics?.market_cap) },
        { label: '52周高', value: fmt(high52w) },
        { label: '52周低', value: fmt(low52w) },
    ]

    // 估值分位 — 使用历史区间计算当前百分位。historical_items 按 date DESC（最新在前）。
    const valItems: ValuationHistoricalItem[] = valuation?.trend?.historical_items ?? []
    const latestVal = valItems.length > 0 ? valItems[0] : null
    const valPctItems = latestVal ? [
        { label: 'PE TTM', value: latestVal.pe_ttm, pct: latestVal.pe_ttm != null ? computePercentile(valItems, 'pe_ttm', latestVal.pe_ttm) : null },
        { label: 'PB MRQ', value: latestVal.pb_mrq, pct: latestVal.pb_mrq != null ? computePercentile(valItems, 'pb_mrq', latestVal.pb_mrq) : null },
        { label: 'PS TTM', value: latestVal.ps_ttm, pct: latestVal.ps_ttm != null ? computePercentile(valItems, 'ps_ttm', latestVal.ps_ttm) : null },
    ].filter(item => item.value != null) : []

    const profile = profileData?.profile ?? null
    const plates = profileData?.plates ?? []
    // 取板块标签：行业板块优先，最多展示 3 个
    const industryPlates = plates.filter(p => p.plate_class === 'INDUSTRY').slice(0, 2)
    const otherPlates = plates.filter(p => p.plate_class !== 'INDUSTRY').slice(0, 1)
    const displayPlates = [...industryPlates, ...otherPlates]

    // 公司名：meta 本地优先（stock_info），profile 补充
    const companyName = metaData?.name || profile?.name || null
    // 市场标签来自代码前缀
    const marketPrefix = stockCode.includes('.') ? stockCode.split('.')[0] : ''
    const MARKET_LABEL: Record<string, string> = { HK: '港股', US: '美股', SH: 'A股', SZ: 'A股' }
    const marketLabel = MARKET_LABEL[marketPrefix] ?? marketPrefix

    // 描述文字：market-module profile 优先，fallback 用本地 meta（indicators_extract）
    const description = profile?.description || profile?.business || metaData?.description || null
    const descPeriod = !profile?.description && !profile?.business ? metaData?.description_period : null
    // 行业/板块标签：profile 板块 > meta sector/industry
    const sectorTag = metaData?.sector && displayPlates.length === 0 ? metaData.sector : null
    const industryTag = metaData?.industry && displayPlates.length === 0 && metaData.industry !== metaData.sector
        ? metaData.industry : null

    // 成立年份 / 员工数格式化
    const establishYear = profile?.establish_date?.slice(0, 4) ?? null
    const employeeCount = profile?.employee_count
        ? profile.employee_count >= 10000
            ? `${(profile.employee_count / 10000).toFixed(0)}万+`
            : String(profile.employee_count)
        : null
    const location = [profile?.city, profile?.country].filter(Boolean).join('，')

    return (
        <div className="new-kb-tab overview-tab">
            <Card size="small" className="new-kb-card company-profile-card">
                    {/* 第一行：公司名 + 代码 + 市场（始终显示） */}
                    <div className="company-profile-header">
                        {companyName && <span className="company-name">{companyName}</span>}
                        <span className="company-code">{stockCode}</span>
                        {marketLabel && <Tag color="default" style={{ marginLeft: 4 }}>{marketLabel}</Tag>}
                    </div>

                    {/* 第二行：行业/板块标签 */}
                    {(displayPlates.length > 0 || sectorTag || industryTag) && (
                        <div className="company-profile-tags">
                            {displayPlates.map(p => (
                                <Tag key={p.plate_id} color={p.plate_class === 'INDUSTRY' ? 'blue' : 'default'}>
                                    {p.plate_name ?? p.plate_id}
                                </Tag>
                            ))}
                            {sectorTag && <Tag color="blue">{sectorTag}</Tag>}
                            {industryTag && <Tag color="geekblue">{industryTag}</Tag>}
                        </div>
                    )}

                    {/* 分割线 */}
                    {description && <div className="company-profile-divider" />}

                    {/* 主营业务描述 */}
                    {description && (
                        <p className="company-profile-desc">{description}</p>
                    )}
                    {descPeriod && (
                        <div className="metrics-date" style={{ marginTop: 4 }}>来源：{descPeriod} 财报</div>
                    )}

                    {/* 底部元数据：总部 / 成立 / 员工 / 上市（VPC 环境才有） */}
                    {(location || establishYear || employeeCount || profile?.listing_date) && (
                        <>
                            <div className="company-profile-divider" />
                            <div className="company-profile-meta">
                                {location && <span><span className="meta-label">总部</span>{location}</span>}
                                {establishYear && <span><span className="meta-label">成立</span>{establishYear}</span>}
                                {employeeCount && <span><span className="meta-label">员工</span>{employeeCount}</span>}
                                {profile?.listing_date && (
                                    <Tooltip title={`${profile.listing_exchange ?? ''} ${profile.listing_market ?? ''}`}>
                                        <span><span className="meta-label">上市</span>{profile.listing_date.slice(0, 10)}</span>
                                    </Tooltip>
                                )}
                            </div>
                        </>
                    )}
                </Card>

            <Card
                title="价格走势"
                size="small"
                className="new-kb-card"
                extra={
                    <div className="period-btns">
                        {PERIODS.map(p => (
                            <Button
                                key={p.days}
                                size="small"
                                type={period === p.days ? 'primary' : 'text'}
                                onClick={() => setPeriod(p.days)}
                            >
                                {p.label}
                            </Button>
                        ))}
                    </div>
                }
            >
                {klineLoading ? (
                    <div className="chart-placeholder"><Spin /></div>
                ) : klines.length === 0 ? (
                    <Empty description="暂无行情数据" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '24px 0' }} />
                ) : (
                    <ReactECharts
                        option={buildCandlestickOption(klines)}
                        style={{ height: 280, width: '100%' }}
                        notMerge
                        lazyUpdate
                    />
                )}
            </Card>

            <Card title="核心指标" size="small" className="new-kb-card">
                <div className="metrics-grid">
                    {metricItems.map(item => (
                        <div key={item.label} className="metric-item">
                            <span className="metric-label">{item.label}</span>
                            <span className="metric-value">{item.value}</span>
                        </div>
                    ))}
                </div>
                {metrics?.report_date && (
                    <div className="metrics-date">数据日期：{metrics.report_date}</div>
                )}
            </Card>

            <Card title="估值分位（近90天）" size="small" className="new-kb-card">
                {valPctItems.length === 0 ? (
                    <Empty description="暂无估值历史数据" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '16px 0' }} />
                ) : (
                    <div className="valuation-percentile">
                        {valPctItems.map(item => (
                            <div key={item.label} className="val-pct-row">
                                <span className="val-pct-label">{item.label}</span>
                                <span className="val-pct-value">{fmt(item.value, 1, 'x')}</span>
                                <Progress
                                    percent={item.pct ?? 0}
                                    strokeColor={pctColor(item.pct)}
                                    size="small"
                                    style={{ flex: 1, margin: '0 8px' }}
                                    format={p => `${p?.toFixed(0)}分位`}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            <Card
                title={latestFinancial
                    ? `最新财报快照（${latestFinancial.fiscal_year ?? ''} ${latestFinancial.report_date ?? ''}）`.trim()
                    : '最新财报快照'
                }
                size="small"
                className="new-kb-card"
            >
                <div className="metrics-grid">
                    <div className="metric-item">
                        <span className="metric-label">营收</span>
                        <span className="metric-value">{fmtLarge(latestFinancial?.revenue)}</span>
                    </div>
                    <div className="metric-item">
                        <span className="metric-label">净利润</span>
                        <span className="metric-value">{fmtLarge(latestFinancial?.net_profit_parent)}</span>
                    </div>
                    <div className="metric-item">
                        <span className="metric-label">毛利率</span>
                        <span className="metric-value">{fmt(latestFinancial?.gross_margin_pct, 1, '%')}</span>
                    </div>
                    <div className="metric-item">
                        <span className="metric-label">净利率</span>
                        <span className="metric-value">{fmt(latestFinancial?.net_margin_pct, 1, '%')}</span>
                    </div>
                    <div className="metric-item">
                        <span className="metric-label">ROE</span>
                        <span className="metric-value">{fmt(latestFinancial?.roe_pct, 1, '%')}</span>
                    </div>
                    <div className="metric-item">
                        <span className="metric-label">货币</span>
                        <span className="metric-value">{latestFinancial?.currency || '--'}</span>
                    </div>
                </div>
            </Card>
        </div>
    )
}
